import { CSSProperties, useEffect, useState } from 'react'
import { HeadFC } from 'gatsby'
import { navigate } from 'gatsby-plugin-intl'

import { ArtisanDefaultSeo } from '..'
import ArtisanLayout from '@src/components/Artisan/ArtisanLayout'
import JumpLink from '@molecules/JumpLink'
import { getArtisanFeatures } from '@models/Artisan'
import { useTranslation } from '@models/Translation'
import { useArticle } from '@models/Article'

import * as style from './style.module.sass'

import circleImage from './assets/circle.webp'

const IndexArrow = () => (
  <svg width="33" height="32" viewBox="0 0 33 32" fill="none">
    <path
      d="M18.2559 22.6667L24.9225 16.0001L18.2559 9.33342"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8.92188 22.6667L15.5885 16.0001L8.92187 9.33342"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const degrees = [0, 63, 135]

const ArtisanFeaturesPage = () => {
  const { locale, t } = useTranslation()
  const { getArticles } = useArticle()

  const [articleIndex, setArticleIndex] = useState<number | undefined>()
  const [degree, setDegree] = useState<number | undefined>(0)

  const features = getArtisanFeatures()
  const feature = features[0]
  const articles = getArticles(feature.articleIds)
  const selectedArticle = articles[articleIndex || 0]

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    if (articleIndex === undefined) {
      setTimeout(() => setDegree(0), 600)
    }

    setDegree(undefined)
  }, [articleIndex])

  useEffect(() => {
    const interval = setInterval(
      () =>
        setDegree(prev => {
          if (prev === undefined) {
            return
          }

          return (prev + 2) % 360
        }),
      600,
    )
    return () => clearInterval(interval)
  }, [])

  return (
    <ArtisanLayout>
      <section className={style.main}>
        <div
          className={style.titleContainer}
          data-show={articleIndex === undefined}
        >
          <div className={style.subTitle}>Special Feature</div>
          <h1>
            百年<small>の</small>道、匠<small>の</small>声
          </h1>
          <div
            className={style.description}
            dangerouslySetInnerHTML={{
              __html: t('artisan.featureDescription2'),
            }}
          />

          <div className={style.featureDetail}>
            <div className={style.number}>
              <small>#</small>
              {feature.id}
            </div>
            <button
              className={style.profileImage}
              onClick={() => setArticleIndex(undefined)}
            >
              <img src={feature.profile} alt={feature.name} />
            </button>
            <div className={style.name}>
              <div
                className={style.title}
                dangerouslySetInnerHTML={{
                  __html: locale === 'ja' ? feature.title : feature.titleEn,
                }}
              />
              {locale === 'ja' && feature.name}
              <div className={style.en}>{feature.nameEn}</div>
            </div>
          </div>

          <div className={style.indexContainer}>
            {articleIndex !== undefined && (
              <>
                <div className={style.index}>
                  {locale === 'ja' ? (
                    <>
                      第<strong>{articleIndex + 1}</strong>回
                    </>
                  ) : (
                    <>
                      Episode <strong>{articleIndex + 1}</strong>
                    </>
                  )}
                </div>
                <div className={style.titleContainer}>
                  <button
                    onClick={() => setArticleIndex(articleIndex - 1)}
                    disabled={articleIndex === 0}
                  >
                    <IndexArrow />
                  </button>
                  <div className={style.title}>{selectedArticle.title}</div>
                  <button
                    onClick={() => setArticleIndex(articleIndex + 1)}
                    disabled={articleIndex === articles.length - 1}
                  >
                    <IndexArrow />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {feature && (
          <>
            <div className={style.chronogical}>
              <div className={style.circleContainer}>
                <img
                  src={circleImage}
                  alt="Circle"
                  className={style.circle}
                  data-rotate={degree !== undefined}
                  data-selected={articleIndex !== undefined}
                  style={
                    {
                      '--degree':
                        articleIndex === undefined
                          ? degree
                          : degrees[articleIndex],
                    } as CSSProperties
                  }
                />
              </div>

              {articles.map((article, index) => {
                const angle = (Math.PI * 2 * degrees[index]) / 360
                const sin = Math.sin(angle).toFixed(2)
                const cos = Math.cos(angle).toFixed(2)

                return (
                  <div
                    className={style.article}
                    key={article.blogId}
                    style={{ '--cos': cos, '--sin': sin } as CSSProperties}
                    data-active={articleIndex === index}
                    data-selected={articleIndex !== undefined}
                  >
                    <button
                      className={style.image}
                      onClick={() => {
                        if (articleIndex === index) {
                          navigate(article.path)
                          return
                        }
                        setArticleIndex(index)
                      }}
                      style={{
                        backgroundImage: `url(${article.thumbnail.url}?w=800)`,
                      }}
                    />
                    <div className={style.index} data-locale={locale}>
                      {locale === 'ja' ? (
                        <>
                          第<strong>{index + 1}</strong>回
                        </>
                      ) : (
                        <>
                          Episode <strong>{index + 1}</strong>
                        </>
                      )}
                    </div>

                    <div className={style.link}>
                      <JumpLink to={article.path}>
                        {t('artisan.readArticle')}
                      </JumpLink>
                    </div>
                  </div>
                )
              })}
            </div>

            <div
              className={style.profile}
              data-show={articleIndex === undefined}
            >
              <div className={style.number}>
                <small>#</small>
                {feature.id}
              </div>
              <button
                className={style.selector}
                onClick={() => setArticleIndex(0)}
              >
                <img
                  src={feature.profile}
                  alt={feature.name}
                  className={style.profileImage}
                />
              </button>
              <div className={style.name}>
                {locale === 'ja' && (
                  <>
                    {feature.name}
                    <br data-only="sp" />
                  </>
                )}
                <span className={style.en}>{feature.nameEn}</span>
              </div>
              <div
                className={style.title}
                dangerouslySetInnerHTML={{
                  __html: locale === 'ja' ? feature.title : feature.titleEn,
                }}
              />
            </div>
          </>
        )}
      </section>
    </ArtisanLayout>
  )
}

export const Head: HeadFC = ({ location }) => (
  <ArtisanDefaultSeo title="Artisan Articles" pathName={location.pathname} />
)

export default ArtisanFeaturesPage
